import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Avatar,
  TextField,
  Button,
  Divider,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
} from "@mui/material";
import {
  Person,
  Lock,
  Security,
  Notifications,
  Save,
  Shield,
  Email,
  Phone,
  Badge as BadgeIcon,
} from "@mui/icons-material";
import { useAppSelector } from "../../State/Store";
import api from "../../config/Api";

export const AdminAccount = () => {
  const { user } = useAppSelector((state) => state.auth || { user: null });

  const [profileData, setProfileData] = useState({
    fullName: user?.fullName || "Admin User",
    email: user?.email || "admin@example.com",
    mobile: user?.mobile || "+254 700 000000",
    role: "Super Admin",
  });

  useEffect(() => {
    if (user) {
      setProfileData({
        fullName: user.fullName || "Admin User",
        email: user.email || "admin@example.com",
        mobile: user.mobile || "+254 700 000000",
        role: user.role === user.role? "Super Admin" : "Admin",
      });
    }
  }, [user]);

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [notifications, setNotifications] = useState({
    emailAlerts: true,
    sellerApprovalAlerts: true,
    systemUpdates: false,
  });

  const [toast, setToast] = useState({ open: false, message: "", severity: "success" as "success" | "error" });

  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProfileData({ ...profileData, [e.target.name]: e.target.value });
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
  };

  const handleNotificationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNotifications({ ...notifications, [e.target.name]: e.target.checked });
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("jwt");
      
      // Destructure and omit 'role' so Spring doesn't fail on enum deserialization
      const { role, ...payload } = profileData;

      await api.patch("/api/users/update", payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setToast({ open: true, message: "Profile details updated successfully!", severity: "success" });
    } catch (error: any) {
      setToast({ open: true, message: error.response?.data?.message || "Failed to update profile", severity: "error" });
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setToast({ open: true, message: "New passwords do not match!", severity: "error" });
      return;
    }
    try {
      const token = localStorage.getItem("jwt");
      await api.put(
        "/api/users/update-password",
        {
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setToast({ open: true, message: "Password updated successfully! Email notification sent.", severity: "success" });
      setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (error: any) {
      setToast({ open: true, message: error.response?.data?.message || "Failed to update password", severity: "error" });
    }
  };

  const handleGenerateRandomPassword = async () => {
    try {
      const token = localStorage.getItem("jwt");
      const response = await api.post(
        "/api/users/reset-password-logged",
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setToast({
        open: true,
        message: `New 10-char password generated & emailed: ${response.data.newPassword}`,
        severity: "success",
      });
    } catch (error: any) {
      setToast({ open: true, message: error.response?.data?.message || "Failed to generate password", severity: "error" });
    }
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      <div>
        <Typography variant="h5" className="font-bold text-slate-800 tracking-tight">
          Admin Account Settings
        </Typography>
        <Typography variant="body2" className="text-slate-500">
          Manage your account profile, credentials, security, and notification preferences.
        </Typography>
      </div>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card className="shadow-xs border border-slate-200/80 rounded-2xl">
            <CardContent className="p-6 text-center">
              <div className="relative inline-block mb-4">
                <Avatar className="bg-violet-600 text-white font-bold w-24 h-24 text-3xl mx-auto shadow-md">
                  {profileData.fullName.charAt(0)}
                </Avatar>
                <span className="absolute bottom-1 right-1 bg-emerald-500 border-2 border-white w-4 h-4 rounded-full" />
              </div>

              <Typography variant="h6" className="font-bold text-slate-800">
                {profileData.fullName}
              </Typography>
              <Typography variant="body2" className="text-violet-600 font-semibold mb-4">
                {profileData.role}
              </Typography>

              <Divider className="my-4" />

              <div className="space-y-3 text-left text-xs text-slate-600">
                <div className="flex items-center gap-2">
                  <Email fontSize="small" className="text-slate-400" />
                  <span>{profileData.email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone fontSize="small" className="text-slate-400" />
                  <span>{profileData.mobile}</span>
                </div>
                <div className="flex items-center gap-2">
                  <BadgeIcon fontSize="small" className="text-slate-400" />
                  <span>Role Permissions: Full Access</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 8 }}>
          <Card className="shadow-xs border border-slate-200/80 rounded-2xl">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Person className="text-violet-600" />
                <Typography variant="h6" className="font-bold text-slate-800 text-base">
                  Personal Information
                </Typography>
              </div>

              <form onSubmit={handleSaveProfile} className="space-y-4">
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Full Name"
                      name="fullName"
                      value={profileData.fullName}
                      onChange={handleProfileChange}
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      name="email"
                      type="email"
                      value={profileData.email}
                      onChange={handleProfileChange}
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      name="mobile"
                      value={profileData.mobile}
                      onChange={handleProfileChange}
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Administrative Role"
                      name="role"
                      value={profileData.role}
                      disabled
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                </Grid>

                <div className="flex justify-end mt-4">
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<Save />}
                    className="bg-violet-600 hover:bg-violet-700 text-white capitalize font-semibold shadow-xs"
                  >
                    Save Changes
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card className="shadow-xs border border-slate-200/80 rounded-2xl h-full">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Lock className="text-violet-600" />
                <Typography variant="h6" className="font-bold text-slate-800 text-base">
                  Change Password
                </Typography>
              </div>

              <form onSubmit={handleUpdatePassword} className="space-y-4">
                <TextField
                  fullWidth
                  label="Current Password"
                  name="currentPassword"
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  variant="outlined"
                  size="small"
                  required
                />
                <TextField
                  fullWidth
                  label="New Password"
                  name="newPassword"
                  type="password"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  variant="outlined"
                  size="small"
                  required
                />
                <TextField
                  fullWidth
                  label="Confirm New Password"
                  name="confirmPassword"
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  variant="outlined"
                  size="small"
                  required
                />

                <div className="flex justify-between items-center pt-2">
                  <Button
                    type="button"
                    variant="outlined"
                    color="secondary"
                    onClick={handleGenerateRandomPassword}
                    className="capitalize font-semibold text-xs"
                  >
                    Generate 10-char Password
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<Shield />}
                    className="bg-slate-900 hover:bg-slate-800 text-white capitalize font-semibold shadow-xs"
                  >
                    Update Password
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card className="shadow-xs border border-slate-200/80 rounded-2xl h-full">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Notifications className="text-violet-600" />
                <Typography variant="h6" className="font-bold text-slate-800 text-base">
                  Admin Notifications
                </Typography>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Typography variant="body2" className="font-semibold text-slate-800">
                      Email Notifications
                    </Typography>
                    <Typography variant="caption" className="text-slate-500 block">
                      Receive weekly administrative summary reports.
                    </Typography>
                  </div>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notifications.emailAlerts}
                        onChange={handleNotificationChange}
                        name="emailAlerts"
                        color="primary"
                      />
                    }
                    label=""
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })} sx={{ width: "100%" }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default AdminAccount;